import { c as _c } from "react/compiler-runtime";
import { graphql, useQuery } from "@opensea/graphql";
import { Flex } from "@opensea/ui-kit";
import { omit } from "es-toolkit/object";
import { useEffect, useMemo, useState } from "react";
import { useTranslations } from "use-intl";
import { useSnapshot } from "valtio";
import { Button } from "../../../../../components/Button";
import { TransactionFullScreen } from "../../../../../components/TransactionFullScreen";
import { TransactionReceipt } from "../../../../../components/TransactionReceipt";
import { TransactionReceiptFragment } from "../../../../../components/TransactionReceipt/TransactionReceipt.fragment";
import { ViewItemsOnProfileButton } from "../../../../../components/ViewItemsOnProfileButton";
import { FlowState } from "../../../../../stores/flowStore";
import { useSetReceiptStatus, useSetReceiptStatusFragment } from "../../../../hooks/useSetReceiptStatus";
const FETCH_INTERVAL = 500;
const query = graphql(`
    query CheckoutFlowStatusQuery(
      $transactionIdentifiers: [TransactionIdentifier!]!
      $itemQuotes: [ItemQuoteInput!]!
      $swapProvider: SwapProviderType!
    ) {
      buyReceipt(
        transactionIdentifiers: $transactionIdentifiers
        itemQuotes: $itemQuotes
        swapProvider: $swapProvider
      ) {
        status
        itemReceipts {
          __typename
          item {
            id
          }
        }
        failedItemReceipts {
          __typename
          item {
            id
          }
        }
        ...useSetReceiptStatus
        ...TransactionReceipt
      }
    }
  `, [TransactionReceiptFragment, useSetReceiptStatusFragment]);
export const CheckoutStatus = t0 => {
  const $ = _c(86);
  const {
    transactionIdentifiers,
    itemQuotes,
    itemImageMap,
    crossChain,
    walletDestination
  } = t0;
  const t = useTranslations("wallet.CheckoutStatus");
  const {
    flow
  } = useSnapshot(FlowState);
  const isClaim = flow?.isClaim ?? false;
  const t1 = crossChain ? "RELAY" : "LOCAL_BUY_NFT";
  let t2;
  if ($[0] !== itemQuotes || $[1] !== t1 || $[2] !== transactionIdentifiers) {
    t2 = {
      query,
      variables: {
        transactionIdentifiers,
        itemQuotes,
        swapProvider: t1
      },
      pause: true
    };
    $[0] = itemQuotes;
    $[1] = t1;
    $[2] = transactionIdentifiers;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const [result, reexecuteQuery] = useQuery(t2);
  let t3;
  if ($[4] !== reexecuteQuery || $[5] !== result.data || $[6] !== result.fetching || $[7] !== transactionIdentifiers.length) {
    t3 = function pollForReceipt() {
      if (result.fetching || result.data?.buyReceipt && result.data.buyReceipt.status !== "PENDING" || transactionIdentifiers.length === 0) {
        return;
      }
      const timerId = setTimeout(() => {
        reexecuteQuery({
          requestPolicy: "network-only"
        });
      }, FETCH_INTERVAL);
      return () => clearTimeout(timerId);
    };
    $[4] = reexecuteQuery;
    $[5] = result.data;
    $[6] = result.fetching;
    $[7] = transactionIdentifiers.length;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const t4 = result.data?.buyReceipt;
  let t5;
  if ($[9] !== crossChain || $[10] !== reexecuteQuery || $[11] !== result.fetching || $[12] !== t4 || $[13] !== transactionIdentifiers.length) {
    t5 = [result.fetching, reexecuteQuery, t4, transactionIdentifiers.length, crossChain];
    $[9] = crossChain;
    $[10] = reexecuteQuery;
    $[11] = result.fetching;
    $[12] = t4;
    $[13] = transactionIdentifiers.length;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  useEffect(t3, t5);
  const receipt = result.data?.buyReceipt;
  useSetReceiptStatus(receipt);
  const [receiptOpen, setReceiptOpen] = useState(false);
  if (!receipt || receipt.status === "PENDING") {
    let t6;
    if ($[15] !== itemImageMap) {
      t6 = Object.values(itemImageMap);
      $[15] = itemImageMap;
      $[16] = t6;
    } else {
      t6 = $[16];
    }
    const pendingImages = t6;
    const t7 = isClaim ? "pending.claimDescription" : "pending.description";
    let t8;
    if ($[17] !== t || $[18] !== t7) {
      t8 = t(t7);
      $[17] = t;
      $[18] = t7;
      $[19] = t8;
    } else {
      t8 = $[19];
    }
    const t9 = isClaim ? "pending.claimTitle" : "pending.title";
    let t10;
    if ($[20] !== t || $[21] !== t9) {
      t10 = t(t9);
      $[20] = t;
      $[21] = t9;
      $[22] = t10;
    } else {
      t10 = $[22];
    }
    let t11;
    if ($[23] !== pendingImages || $[24] !== t10 || $[25] !== t8) {
      t11 = <TransactionFullScreen body={t8} images={pendingImages} loading={true} title={t10} />;
      $[23] = pendingImages;
      $[24] = t10;
      $[25] = t8;
      $[26] = t11;
    } else {
      t11 = $[26];
    }
    return t11;
  }
  if (receipt.status === "FAILED") {
    const t6 = isClaim ? "failed.claimDescription" : "failed.description";
    let t7;
    if ($[27] !== t || $[28] !== t6) {
      t7 = t(t6);
      $[27] = t;
      $[28] = t6;
      $[29] = t7;
    } else {
      t7 = $[29];
    }
    let t8;
    if ($[30] !== receipt || $[31] !== receiptOpen || $[32] !== t || $[33] !== walletDestination) {
      t8 = receiptOpen ? <TransactionReceipt flowType="purchase" receipt={receipt} walletDestination={walletDestination} /> : <Button onClick={() => setReceiptOpen(true)} variant="secondary">{t("viewReceipt")}</Button>;
      $[30] = receipt;
      $[31] = receiptOpen;
      $[32] = t;
      $[33] = walletDestination;
      $[34] = t8;
    } else {
      t8 = $[34];
    }
    let t9;
    if ($[35] !== itemImageMap || $[36] !== receipt.failedItemReceipts) {
      let t10;
      if ($[38] !== itemImageMap) {
        t10 = item => item.item ? itemImageMap[item.item.id] : null;
        $[38] = itemImageMap;
        $[39] = t10;
      } else {
        t10 = $[39];
      }
      t9 = receipt.failedItemReceipts.map(t10);
      $[35] = itemImageMap;
      $[36] = receipt.failedItemReceipts;
      $[37] = t9;
    } else {
      t9 = $[37];
    }
    const t10 = isClaim ? "failed.claimTitle" : "failed.title";
    let t11;
    if ($[40] !== t || $[41] !== t10) {
      t11 = t(t10);
      $[40] = t;
      $[41] = t10;
      $[42] = t11;
    } else {
      t11 = $[42];
    }
    let t12;
    if ($[43] !== t11 || $[44] !== t7 || $[45] !== t8 || $[46] !== t9) {
      t12 = <TransactionFullScreen body={t7} bottom={t8} images={t9} monochrome={true} title={t11} />;
      $[43] = t11;
      $[44] = t7;
      $[45] = t8;
      $[46] = t9;
      $[47] = t12;
    } else {
      t12 = $[47];
    }
    return t12;
  }
  if (receipt.status === "PARTIAL_SUCCESS") {
    let t6;
    if ($[48] !== receipt.failedItemReceipts.length || $[49] !== receipt.itemReceipts.length || $[50] !== t) {
      t6 = t("partialSuccess.description", {
        itemsPurchasedCount: receipt.itemReceipts.length,
        itemsFailedCount: receipt.failedItemReceipts.length
      });
      $[48] = receipt.failedItemReceipts.length;
      $[49] = receipt.itemReceipts.length;
      $[50] = t;
      $[51] = t6;
    } else {
      t6 = $[51];
    }
    let t7;
    if ($[52] !== receipt || $[53] !== receiptOpen || $[54] !== t || $[55] !== walletDestination) {
      t7 = receiptOpen ? <TransactionReceipt flowType="purchase" receipt={receipt} walletDestination={walletDestination} /> : <Flex className="gap-4"><Button onClick={() => setReceiptOpen(true)} variant="secondary">{t("viewReceipt")}</Button><ViewItemsOnProfileButton itemIds={receipt.itemReceipts.flatMap(_temp)} walletDestinationAddress={walletDestination?.address} /></Flex>;
      $[52] = receipt;
      $[53] = receiptOpen;
      $[54] = t;
      $[55] = walletDestination;
      $[56] = t7;
    } else {
      t7 = $[56];
    }
    let t8;
    if ($[57] !== itemImageMap || $[58] !== receipt.itemReceipts) {
      let t9;
      if ($[60] !== itemImageMap) {
        t9 = item_0 => item_0.item ? itemImageMap[item_0.item.id] : null;
        $[60] = itemImageMap;
        $[61] = t9;
      } else {
        t9 = $[61];
      }
      t8 = receipt.itemReceipts.map(t9);
      $[57] = itemImageMap;
      $[58] = receipt.itemReceipts;
      $[59] = t8;
    } else {
      t8 = $[59];
    }
    let t9;
    if ($[62] !== t) {
      t9 = t("partialSuccess.title");
      $[62] = t;
      $[63] = t9;
    } else {
      t9 = $[63];
    }
    let t10;
    if ($[64] !== t6 || $[65] !== t7 || $[66] !== t8 || $[67] !== t9) {
      t10 = <TransactionFullScreen body={t6} bottom={t7} images={t8} title={t9} />;
      $[64] = t6;
      $[65] = t7;
      $[66] = t8;
      $[67] = t9;
      $[68] = t10;
    } else {
      t10 = $[68];
    }
    return t10;
  }
  let t6;
  if ($[69] !== receipt || $[70] !== receiptOpen || $[71] !== t || $[72] !== walletDestination) {
    t6 = receiptOpen ? <TransactionReceipt flowType="purchase" receipt={receipt} walletDestination={walletDestination} /> : <Flex className="gap-4"><Button onClick={() => setReceiptOpen(true)} variant="secondary">{t("viewReceipt")}</Button><ViewItemsOnProfileButton itemIds={receipt.itemReceipts.flatMap(_temp2)} walletDestinationAddress={walletDestination?.address} /></Flex>;
    $[69] = receipt;
    $[70] = receiptOpen;
    $[71] = t;
    $[72] = walletDestination;
    $[73] = t6;
  } else {
    t6 = $[73];
  }
  let t7;
  if ($[74] !== itemImageMap || $[75] !== receipt.itemReceipts) {
    let t8;
    if ($[77] !== itemImageMap) {
      t8 = item_1 => item_1.item ? itemImageMap[item_1.item.id] : null;
      $[77] = itemImageMap;
      $[78] = t8;
    } else {
      t8 = $[78];
    }
    t7 = receipt.itemReceipts.map(t8);
    $[74] = itemImageMap;
    $[75] = receipt.itemReceipts;
    $[76] = t7;
  } else {
    t7 = $[76];
  }
  const t8 = isClaim ? "success.claimTitle" : "success.title";
  let t9;
  if ($[79] !== t || $[80] !== t8) {
    t9 = t(t8);
    $[79] = t;
    $[80] = t8;
    $[81] = t9;
  } else {
    t9 = $[81];
  }
  let t10;
  if ($[82] !== t6 || $[83] !== t7 || $[84] !== t9) {
    t10 = <TransactionFullScreen bottom={t6} confetti={true} images={t7} title={t9} />;
    $[82] = t6;
    $[83] = t7;
    $[84] = t9;
    $[85] = t10;
  } else {
    t10 = $[85];
  }
  return t10;
};
export const CheckoutFlowStatus = t0 => {
  const $ = _c(10);
  const {
    walletDestination
  } = t0;
  const {
    receiptTransactions,
    itemQuotes,
    itemImageMap,
    flow
  } = useSnapshot(FlowState);
  let t1;
  let t2;
  if ($[0] !== itemQuotes) {
    t2 = itemQuotes.map(_temp3);
    $[0] = itemQuotes;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  t1 = t2;
  const itemQuoteInputs = t1;
  if (flow?.type !== "checkout") {
    throw new Error("Not supported flow type");
  }
  const t3 = flow.crossChain;
  let t4;
  if ($[2] !== receiptTransactions) {
    t4 = receiptTransactions.map(_temp4);
    $[2] = receiptTransactions;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== flow.crossChain || $[5] !== itemImageMap || $[6] !== itemQuoteInputs || $[7] !== t4 || $[8] !== walletDestination) {
    t5 = <CheckoutStatus crossChain={t3} itemImageMap={itemImageMap} itemQuotes={itemQuoteInputs} transactionIdentifiers={t4} walletDestination={walletDestination} />;
    $[4] = flow.crossChain;
    $[5] = itemImageMap;
    $[6] = itemQuoteInputs;
    $[7] = t4;
    $[8] = walletDestination;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
};
function _temp(itemReceipt) {
  return itemReceipt.item ? [itemReceipt.item.id] : [];
}
function _temp2(itemReceipt_0) {
  return itemReceipt_0.item ? [itemReceipt_0.item.id] : [];
}
function _temp3(itemQuote) {
  return omit(itemQuote, ["orderId"]);
}
function _temp4(txn) {
  return {
    chain: txn.chain,
    transactionHash: txn.hash
  };
}